import { Skeleton } from "antd";
import { useEffect } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
} from "recharts";
import "./style.css";

// ── Palette ───────────────────────────────────────────
const COLORS = [
    "var(--color-primary)",
    "var(--color-accent)",
    "var(--color-warning)",
    "var(--color-primary-hover)",
    "var(--color-success)",
    "var(--color-error)",
];

// ── Data transforms ───────────────────────────────────
function transformPieData(rawData) {
    if (!rawData || rawData.length < 2) return [];
    const [, ...rows] = rawData;
    return rows.map(([name, value]) => ({
        name: String(name),
        value: Number(value) || 0,
    }));
}

function transformBarData(rawData) {
    if (!rawData || rawData.length < 2) return { data: [], keys: [] };
    const [headers, ...rows] = rawData;
    const [xKey, ...seriesKeys] = headers;
    const data = rows.map((row) => {
        const obj = { [xKey]: String(row[0]) };
        seriesKeys.forEach((k, i) => {
            obj[k] = Number(row[i + 1]) || 0;
        });
        return obj;
    });
    return { data, keys: seriesKeys, xKey };
}

// ── Shared styles ─────────────────────────────────────
const axisStyle = {
    tick: { fill: "var(--color-text-muted)", fontSize: 11 },
    axisLine: { stroke: "var(--color-border)" },
    tickLine: false,
};

// ── Custom Tooltip ────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "var(--color-bg-control)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
            }}
        >
            {label && (
                <p
                    style={{
                        margin: 0,
                        fontWeight: 600,
                        marginBottom: 6,
                        color: "var(--color-text-secondary)",
                        fontSize: 11,
                    }}
                >
                    {label}
                </p>
            )}
            {payload.map((entry) => (
                <p
                    key={entry.name}
                    style={{
                        margin: "2px 0",
                        color: entry.color || entry.fill,
                    }}
                >
                    {entry.name}:{" "}
                    <strong style={{ color: "var(--color-text)" }}>
                        {entry.value}
                    </strong>
                </p>
            ))}
        </div>
    );
};

// ── Pie label renderer ────────────────────────────────
const renderPieLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
}) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text
            x={x}
            y={y}
            fill="var(--color-text-on-primary)"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fontWeight={600}
        >
            {`${(percent * 100).toFixed(1)}%`}
        </text>
    );
};

// ── Reusable pie chart ────────────────────────────────
function DemoPieChart({ rawData, title, height = 280 }) {
    const data = transformPieData(rawData);
    if (!data.length) return <Skeleton title={false} paragraph={{ rows: 6 }} />;
    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <p
                style={{
                    textAlign: "center",
                    color: "var(--color-text-secondary)",
                    fontWeight: 600,
                    fontSize: 13,
                    marginBottom: 4,
                }}
            >
                {title}
            </p>
            <ResponsiveContainer width="100%" height={height}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        outerRadius={height * 0.38}
                        dataKey="value"
                        labelLine={false}
                        label={renderPieLabel}
                    >
                        {data.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        wrapperStyle={{ fontSize: 12 }}
                        formatter={(v) => (
                            <span style={{ color: "var(--color-text-secondary)" }}>
                                {v}
                            </span>
                        )}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}

// ── Reusable bar chart ────────────────────────────────
function DemoBarChart({ rawData, title, stacked = false, height = 300 }) {
    const { data, keys, xKey } = transformBarData(rawData);
    if (!data.length) return <Skeleton title={false} paragraph={{ rows: 6 }} />;
    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <p
                style={{
                    textAlign: "center",
                    color: "var(--color-text-secondary)",
                    fontWeight: 600,
                    fontSize: 13,
                    marginBottom: 4,
                }}
            >
                {title}
            </p>
            <ResponsiveContainer width="100%" height={height}>
                <BarChart
                    data={data}
                    margin={{ top: 5, right: 16, left: 0, bottom: 5 }}
                    barCategoryGap="35%"
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--color-border-light)"
                        vertical={false}
                    />
                    <XAxis dataKey={xKey} {...axisStyle} />
                    <YAxis {...axisStyle} allowDecimals={false} />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "var(--color-primary-light)" }}
                    />
                    <Legend
                        wrapperStyle={{ fontSize: 12 }}
                        formatter={(v) => (
                            <span style={{ color: "var(--color-text-secondary)" }}>
                                {v}
                            </span>
                        )}
                    />
                    {keys.map((key, i) => (
                        <Bar
                            key={key}
                            dataKey={key}
                            fill={COLORS[i % COLORS.length]}
                            radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
                            stackId={stacked ? "stack" : undefined}
                            maxBarSize={52}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

// ── Section wrapper ───────────────────────────────────
function Section({ title, children, style = {} }) {
    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
                marginBottom: 20,
                ...style,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        width: 3,
                        height: 18,
                        borderRadius: 2,
                        background: "var(--gradient-primary)",
                    }}
                />
                <span
                    style={{
                        color: "var(--color-text)",
                        fontWeight: 600,
                        fontSize: 15,
                    }}
                >
                    {title}
                </span>
            </div>
            {children}
        </div>
    );
}

// ── Stat pill ─────────────────────────────────────────
function StatPill({ label, value, color = "var(--color-primary)" }) {
    return (
        <div
            style={{
                background: "var(--color-bg-control)",
                border: `1px solid ${color}33`,
                borderRadius: 10,
                padding: "16px 28px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                minWidth: 130,
            }}
        >
            <span style={{ fontSize: 26, fontWeight: 700, color }}>
                {value}
            </span>
            <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                {label}
            </span>
        </div>
    );
}

// ── Main component ────────────────────────────────────
function ChartComponent() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");

    const mkQuery = (key, url) =>
        useQuery(key, async () => await HTTP(url, { staff_id }), {
            enabled: false,
        });

    const {
        status: genderChartStatus,
        data: genderChartRes,
        refetch: getGenderChart,
    } = mkQuery("genderChart", "/analytics/getPlatformUsedbyGenderData");
    const {
        status: ageChartStatus,
        data: ageChartRes,
        refetch: getAgeChart,
    } = mkQuery("ageChart", "/analytics/getPlatformUsedbyAgeData");
    const {
        status: guestAgeStatus,
        data: guestAgeRes,
        refetch: getGuestAgeChart,
    } = mkQuery("guestAgeChart", "/analytics/getGuestsServedAgeData");
    const {
        status: reviewsAgeStatus,
        data: reviewsAgeRes,
        refetch: getReviewsAgeChart,
    } = mkQuery("reviewsAgeChart", "/analytics/getReviewsAgeData");
    const {
        status: roomChartStatus,
        data: roomChartRes,
        refetch: getRoomChart,
    } = mkQuery("roomChart", "/analytics/getRoomsData");

    useEffect(() => {
        getGenderChart();
        getAgeChart();
        getGuestAgeChart();
        getReviewsAgeChart();
        getRoomChart();
    }, []);

    const isReady = (s) => s !== "loading" && s !== "idle";

    return (
        <div style={{ padding: "4px 0" }}>
            {/* ── Section 1: Who Used Our Platform ── */}
            <Section title="Who Used Our Platform?">
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                    {isReady(ageChartStatus) ? (
                        <DemoPieChart
                            rawData={ageChartRes?.data}
                            title="Platform Usage by Age Group"
                            height={300}
                        />
                    ) : (
                        <div style={{ flex: 1 }}>
                            <Skeleton title={false} paragraph={{ rows: 8 }} />
                        </div>
                    )}
                    {isReady(genderChartStatus) ? (
                        <DemoPieChart
                            rawData={genderChartRes?.data}
                            title="Platform Usage by Gender"
                            height={300}
                        />
                    ) : (
                        <div style={{ flex: 1 }}>
                            <Skeleton title={false} paragraph={{ rows: 8 }} />
                        </div>
                    )}
                </div>
            </Section>

            {/* ── Section 2: Customer Matrix ── */}
            <Section title="Customer Matrix">
                {isReady(roomChartStatus) ? (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 32,
                            flexWrap: "wrap",
                        }}
                    >
                        <div style={{ flex: 1, minWidth: 260 }}>
                            <DemoPieChart
                                rawData={roomChartRes?.data?.chartData}
                                title="Room Distribution"
                                height={280}
                            />
                        </div>
                        <div
                            style={{
                                display: "flex",
                                gap: 16,
                                flexWrap: "wrap",
                                justifyContent: "center",
                            }}
                        >
                            <StatPill
                                label="Total Hotels"
                                value={roomChartRes?.data?.hotels_total ?? "—"}
                                color="var(--color-primary)"
                            />
                            <StatPill
                                label="Total Rooms"
                                value={roomChartRes?.data?.rooms_total ?? "—"}
                                color="var(--color-accent)"
                            />
                        </div>
                    </div>
                ) : (
                    <Skeleton title={false} paragraph={{ rows: 8 }} />
                )}
            </Section>

            {/* ── Section 3: Age Groups ── */}
            <Section title="Age Groups">
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                    {isReady(guestAgeStatus) ? (
                        <DemoBarChart
                            rawData={guestAgeRes?.data}
                            title="Guests Served by Age"
                            height={320}
                        />
                    ) : (
                        <div style={{ flex: 1 }}>
                            <Skeleton title={false} paragraph={{ rows: 8 }} />
                        </div>
                    )}
                    {isReady(reviewsAgeStatus) ? (
                        <DemoBarChart
                            rawData={reviewsAgeRes?.data}
                            title="Reviews by Age"
                            height={320}
                            stacked
                        />
                    ) : (
                        <div style={{ flex: 1 }}>
                            <Skeleton title={false} paragraph={{ rows: 8 }} />
                        </div>
                    )}
                </div>
            </Section>
        </div>
    );
}

export default ChartComponent;
